/**
 * RTWO Cryptography & Identity Engine — Event Bus
 */

import type { CryptoEventType } from '../domain-models';

export const CryptoEventTypes = {
  KEY_PAIR_REQUESTED: 'key_pair_requested',
  KEY_PAIR_GENERATED: 'key_pair_generated',
  KEY_ACTIVATED: 'key_activated',
  KEY_ROTATION_STARTED: 'key_rotation_started',
  KEY_ROTATED: 'key_rotated',
  KEY_REVOKED: 'key_revoked',
  KEY_COMPROMISED: 'key_compromised',
  KEY_ARCHIVED: 'key_archived',
  ADDRESS_DERIVED: 'address_derived',
  SIGNATURE_CREATED: 'signature_created',
  SIGNATURE_VERIFIED: 'signature_verified',
  IDENTITY_CREATED: 'identity_created',
  IDENTITY_VERIFIED: 'identity_verified',
  IDENTITY_SUSPENDED: 'identity_suspended',
  IDENTITY_REVOKED: 'identity_revoked',
  TRUST_RECORD_CREATED: 'trust_record_created',
  TRUST_RECORD_UPDATED: 'trust_record_updated',
  KEY_PROVIDER_REGISTERED: 'key_provider_registered',
  KEY_PROVIDER_CONNECTED: 'key_provider_connected',
  IDENTITY_BACKEND_REGISTERED: 'identity_backend_registered',
  IDENTITY_BACKEND_CONNECTED: 'identity_backend_connected',
  CONFIGURATION_BOUND: 'configuration_bound',
  REPOSITORIES_BOOTSTRAPPED: 'repositories_bootstrapped',
} as const satisfies Record<string, CryptoEventType>;

export interface CryptoBusEvent<T = unknown> {
  id: string;
  type: CryptoEventType;
  entityId: string;
  payload: T;
  timestamp: number;
}

export type CryptoBusEventHandler<T = unknown> = (event: CryptoBusEvent<T>) => void;

class CryptoEventBus {
  private subscriptions = new Map<CryptoEventType, CryptoBusEventHandler[]>();

  subscribe<T>(eventType: CryptoEventType, handler: CryptoBusEventHandler<T>): () => void {
    const handlers = this.subscriptions.get(eventType) ?? [];
    handlers.push(handler as CryptoBusEventHandler);
    this.subscriptions.set(eventType, handlers);

    return () => {
      const current = this.subscriptions.get(eventType) ?? [];
      this.subscriptions.set(
        eventType,
        current.filter((entry) => entry !== handler)
      );
    };
  }

  emit<T>(eventType: CryptoEventType, entityId: string, payload: T): string {
    const event: CryptoBusEvent<T> = {
      id: `cryp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type: eventType,
      entityId,
      payload,
      timestamp: Date.now(),
    };

    const handlers = this.subscriptions.get(eventType) ?? [];
    for (const handler of handlers) {
      try {
        handler(event);
      } catch (error) {
        console.error(`[CryptoEventBus] Handler error for ${eventType}:`, error);
      }
    }

    return event.id;
  }
}

export const cryptoEventBus = new CryptoEventBus();
